import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  deleteTaxCategoryField,
  getAllTaxCategory,
  getTaxCategoryFields,
  ITaxCategory,
} from "./TaxCategoryController";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import { IPagination } from "../../data/AppInterface";
import { deleteIcon, editIcon, eyeIcon } from "../../assets/icons/SvgIconList";
import {
  AdvanceSearch,
  ICConfirmationModal,
  ICTable,
} from "../../core-component/ui";
import { prepareMessageFromParams } from "../../utils/AppFunctions";
import { ORDER_BY } from "../../utils/AppConstants";
import TaxCategoryForm from "./TaxCategoryForm";

let timer: ReturnType<typeof setTimeout>;

const TaxCategoryView = () => {
  const { t } = UseTranslationHook();
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const [taxCategoryList, setTaxCategoryList] = useState<ITaxCategory[]>([]);
  const { pagination, setChangePagination } = UsePaginationHook();
  const [taxCategoryToEdit, setTaxCategoryToEdit] = useState<ITaxCategory>();
  useEffect(() => {
    fetchTaxCategoryField({ ...pagination, currentPage: 1 });
  }, [enterpriseSearch]);

  const fetchTaxCategoryField = (paginationPlayLoad: IPagination) => {
    setIsLoading(true);
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      await getAllTaxCategory(
        setTaxCategoryList,
        paginationPlayLoad,
        setChangePagination,
        enterpriseSearch,
      );
      setIsLoading(false);
    }, 500);
  };

  const customActionView = (item: ITaxCategory) => {
    return (
      <div className="flex item-center justify-center w-full">
        <div
          className="size-5 cursor-pointer"
          title={t("view_tooltip_icon")}
          onClick={() => {
            setTaxCategoryToEdit(item);
            setReadOnly(true);
            setShowForm(true);
          }}
        >
          {eyeIcon}
        </div>

        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("edit_tooltip_icon")}
          onClick={() => {
            setShowForm(true);
            setTaxCategoryToEdit(item);
          }}
        >
          {editIcon}
        </div>
        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("delete_tooltip_icon")}
          onClick={() => {
            setShowDeleteConfirmation(true);
            setTaxCategoryToEdit(item);
          }}
        >
          {deleteIcon}
        </div>
      </div>
    );
  };

  const handleDelete = async (value: boolean) => {
    if (value && taxCategoryToEdit) {
      const data = await deleteTaxCategoryField(taxCategoryToEdit.id);
      if (data) {
        fetchTaxCategoryField(pagination);
      }
    }
    setShowDeleteConfirmation(false);
    setTaxCategoryToEdit(undefined);
  };
  const handleCloseForm = (status: boolean) => {
    setShowForm(false);
    setReadOnly(false);
    if (taxCategoryToEdit) {
      setTaxCategoryToEdit(undefined);
    }
    if (status) {
      fetchTaxCategoryField(pagination);
    }
  };

  return (
    <div className="w-full overflow-scroll scrollbar-remove p-4">
      <AdvanceSearch
        handleOnClickAdd={() => setShowForm(true)}
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        searchText={enterpriseSearch}
        refreshTable={() => {
          fetchTaxCategoryField(pagination);
        }}
        title={t("tax_category")}
      />

      <ICTable<ITaxCategory>
        isLoading={isLoading}
        totalFlex="grid grid-cols-14"
        fields={getTaxCategoryFields(customActionView, t)}
        data={taxCategoryList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchTaxCategoryField({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchTaxCategoryField({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchTaxCategoryField({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />

      {showForm && (
        <TaxCategoryForm
          handleClose={handleCloseForm}
          taxCategoryToEdit={taxCategoryToEdit}
          readOnly={readOnly}
        />
      )}
      {showDeleteConfirmation && taxCategoryToEdit && (
        <ICConfirmationModal
          title={t("tax_category")}
          message={prepareMessageFromParams(t("delete_message"), [
            ["fieldName", taxCategoryToEdit.category_name],
          ])}
          onClose={(value) => handleDelete(value)}
        />
      )}
    </div>
  );
};

export default TaxCategoryView;
